/**
 * Index-space views of a graph, derived once and read by every layout.
 *
 * Everything downstream works in indices rather than ids: a layout touches every pair of nodes on
 * every iteration, and resolving a string key that many times is the difference between a layout
 * that runs while you drag a control and one that does not. Ids are resolved here, once.
 */

import type { NetworkGraph, NetworkNode, NetworkNodeId } from '../types';

/** An edge in index space. Edges naming a node the graph does not contain are dropped. */
export type Link = {
  source: number;
  target: number;
};

const toNodeIndex = (
  nodes: readonly NetworkNode[]
): Map<NetworkNodeId, number> =>
  new Map(nodes.map((node, index) => [node.id, index]));

/**
 * The edges that survive, in index space, with where each came from.
 *
 * The two lists are returned together rather than derived separately because they have to agree:
 * `edgeIndices[n]` is the entry of `graph.edges` that produced `links[n]`, and a consumer that
 * needs a reference's own color or line style reads it through that rather than by guessing that
 * nothing was dropped.
 */
export type LinkedEdges = {
  links: Link[];
  edgeIndices: number[];
};

export const toLinks = ({ nodes, edges }: NetworkGraph): LinkedEdges => {
  const indexOf = toNodeIndex(nodes);
  const links: Link[] = [];
  const edgeIndices: number[] = [];

  edges.forEach(({ sourceId, targetId }, edgeIndex) => {
    const source = indexOf.get(sourceId);
    const target = indexOf.get(targetId);

    /* A self-loop is dropped rather than laid out. It has no direction to contribute to any
       layout, and a spring of length zero between a node and itself divides by its own distance. */
    if (source === undefined || target === undefined || source === target) {
      return;
    }
    links.push({ source, target });
    edgeIndices.push(edgeIndex);
  });

  return { links, edgeIndices };
};

export const toDegrees = (
  nodeCount: number,
  links: readonly Link[]
): number[] => {
  const degrees = new Array<number>(nodeCount).fill(0);

  for (const { source, target } of links) {
    degrees[source] += 1;
    degrees[target] += 1;
  }
  return degrees;
};

/**
 * The parent/child structure, as a forest.
 *
 * A forest rather than a tree because the fixture has three roots and a real workspace set has
 * more - so `rootIndices` is a list, and a node whose parent is missing or whose parent chain
 * cycles is treated as a root rather than crashing a traversal. Both cases are reachable from live
 * data: a reference to a component outside the loaded scope, and a cycle introduced by an import.
 */
export type Hierarchy = {
  parentIndex: (number | null)[];
  childIndices: number[][];
  rootIndices: number[];
  /** 0 at a root. */
  depths: number[];
  /** Leaves beneath each node; 1 at a leaf. What a tree layout divides angular space by. */
  leafCounts: number[];
  maxDepth: number;
};

export const toHierarchy = (nodes: readonly NetworkNode[]): Hierarchy => {
  const indexOf = toNodeIndex(nodes);
  const parentIndex = nodes.map(({ parentId }) => {
    if (parentId === null) return null;
    return indexOf.get(parentId) ?? null;
  });

  const childIndices: number[][] = nodes.map(() => []);
  const rootIndices: number[] = [];

  parentIndex.forEach((parent, index) => {
    if (parent === null || parent === index) {
      rootIndices.push(index);
      return;
    }
    childIndices[parent].push(index);
  });

  const depths = new Array<number>(nodes.length).fill(0);
  const leafCounts = new Array<number>(nodes.length).fill(0);
  const visited = new Array<boolean>(nodes.length).fill(false);
  let maxDepth = 0;

  /* Iterative rather than recursive: the depth of an imported estate is not bounded by anything,
     and a stack overflow inside a layout is a blank view with no message. */
  const stack: { index: number; depth: number; isExit: boolean }[] =
    rootIndices.map(index => ({ index, depth: 0, isExit: false }));

  while (stack.length > 0) {
    const frame = stack.pop();
    if (!frame) break;
    const { index, depth, isExit } = frame;

    if (isExit) {
      const children = childIndices[index];
      leafCounts[index] =
        children.length === 0
          ? 1
          : children.reduce((total, child) => total + leafCounts[child], 0);
      continue;
    }

    /* A cycle reaches an already-visited node. Left where it is rather than re-parented: the
       traversal simply does not descend twice, so its leaf count stays 0 and every layout treats
       it as the leaf it effectively is. */
    if (visited[index]) continue;
    visited[index] = true;
    depths[index] = depth;
    maxDepth = Math.max(maxDepth, depth);

    stack.push({ index, depth, isExit: true });
    for (const child of childIndices[index]) {
      stack.push({ index: child, depth: depth + 1, isExit: false });
    }
  }

  /* Anything the traversal never reached sits in a cycle with no root above it. Promoted to a root
     so no layout is handed a node with no position. */
  visited.forEach((wasVisited, index) => {
    if (!wasVisited) {
      rootIndices.push(index);
      leafCounts[index] = 1;
    }
  });

  return {
    parentIndex,
    childIndices,
    rootIndices,
    depths,
    leafCounts,
    maxDepth,
  };
};

/**
 * Every node, parents before children, roots in index order, each one once.
 *
 * Here rather than in the one layout that first needed it, because reversing it is how anything
 * accumulates up a tree - a group's radius from its children's, a region's contents from theirs -
 * and two copies of a traversal that has to skip a cycle the same way is two chances to skip it
 * differently.
 *
 * Iterative for the reason `toHierarchy` gives: the depth of an imported estate is not bounded by
 * anything, and a stack overflow inside a layout is a blank view with no message.
 */
export const toTopDownOrder = ({
  childIndices,
  rootIndices,
}: Hierarchy): number[] => {
  const order: number[] = [];
  const stack = [...rootIndices].reverse();
  const seen = new Set<number>();

  while (stack.length > 0) {
    const index = stack.pop();
    if (index === undefined || seen.has(index)) continue;
    seen.add(index);
    order.push(index);

    const children = childIndices[index];
    for (let at = children.length - 1; at >= 0; at--) {
      stack.push(children[at]);
    }
  }

  return order;
};

/**
 * The chain from the root down to `index`, inclusive at both ends.
 *
 * What edge bundling routes along: an edge that follows the hierarchy up to the common ancestor and
 * back down is an edge that shares its middle with every other edge between the same two subtrees,
 * which is what makes a bundle.
 */
export const toAncestorPath = (
  { parentIndex }: Hierarchy,
  index: number
): number[] => {
  const path: number[] = [];
  const seen = new Set<number>();
  let current: number | null = index;

  while (current !== null && !seen.has(current)) {
    seen.add(current);
    path.push(current);
    current = parentIndex[current];
  }

  return path.reverse();
};

/**
 * The two paths joined at their lowest common ancestor, source-first.
 *
 * Returns the full route including both endpoints. Two nodes in different roots share no ancestor,
 * so the path runs up to one root and down from the other - which draws as an edge over the top of
 * the structure rather than through it, and is the honest picture of a cross-workspace reference.
 */
export const toBundlePath = (
  hierarchy: Hierarchy,
  source: number,
  target: number
): number[] => {
  const fromSource = toAncestorPath(hierarchy, source);
  const fromTarget = toAncestorPath(hierarchy, target);

  let shared = 0;
  while (
    shared < fromSource.length &&
    shared < fromTarget.length &&
    fromSource[shared] === fromTarget[shared]
  ) {
    shared += 1;
  }

  /* One shared ancestor is kept as the turning point. With none - separate roots - the join is the
     two roots in sequence, and the curve passes between them. */
  const keep = Math.max(0, shared - 1);

  return [...fromSource.slice(keep).reverse(), ...fromTarget.slice(shared)];
};
